import React from 'react'
import { H3, Flex, H5 } from '../../lib/components'
import { DashboardSummary, OpportunityDetails, WithdrawalApprovalDetails } from '../../lib/types'
import InvestmentDetailsCard from '../dashboard-new/investments-details-card'

interface WithdrawalAmountOpportunityCardProps {
  opportunity: OpportunityDetails
  investmentSummary: DashboardSummary
  withdrawalDetails?: WithdrawalApprovalDetails | null
  isLoadingWithdrawalDetails?: boolean
  withdrawalAmount?: string
}

const WithdrawalAmountOpportunityCard = ({
  opportunity,
  investmentSummary,
  withdrawalDetails,
  isLoadingWithdrawalDetails = false,
  withdrawalAmount,
}: WithdrawalAmountOpportunityCardProps) => {
  const formatCurrency = (value: number | undefined): string => {
    if (isNaN(Number(value)) || value === undefined) return '$0.00'
    return `$${Number(value / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatPercentage = (value: number | undefined): string => {
    if (isNaN(Number(value)) || value === undefined) return '0.00%'
    return `${Number(value).toFixed(2)}%`
  }

  const formatCurrencyFromDollar = (value: string | number | undefined): string => {
    if (!value || isNaN(Number(value))) return '$0.00'
    return `$${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  return (
    <div className='withdraw-amount-opportunity-card'>
      <H3 text={opportunity?.projectName} />

      {/* Investment Summary Cards */}
      <Flex classes={['row-center', 'mt-32', 'w-100', 'withdraw-amount-opportunity-card-detail']}>
        <InvestmentDetailsCard
          cardText='total-investments'
          value={
            isNaN(Number(investmentSummary?.totalInvestedAmount))
              ? 0
              : Number(investmentSummary?.totalInvestedAmount)
          }
        />
        <InvestmentDetailsCard
          cardText='total-returns'
          value={
            isNaN(Number(investmentSummary?.totalReturnsTillDate))
              ? 0
              : Number(investmentSummary?.totalReturnsTillDate)
          }
        />
        <InvestmentDetailsCard
          cardText='realized-gains'
          value={
            isNaN(Number(investmentSummary?.realizedGains))
              ? 0
              : Number(investmentSummary?.realizedGains)
          }
        />
        <InvestmentDetailsCard
          cardText='unrealized-gains'
          value={
            isNaN(Number(investmentSummary?.unrealizedGains))
              ? 0
              : Number(investmentSummary?.unrealizedGains)
          }
        />
      </Flex>

      {/* Withdrawal Approval Details Section */}
      {(withdrawalDetails || isLoadingWithdrawalDetails) && (
        <div className='mt-48' style={{ width: '100%' }}>
          <H5 text='Redemption Details Preview' />

          {isLoadingWithdrawalDetails ? (
            <div className='withdrawal-details-loading mt-24'>
              <div className='loading-spinner'></div>
              <p>Loading redemption details...</p>
            </div>
          ) : withdrawalDetails ? (
            <div className='withdrawal-approval-details-section mt-24'>
              <div className='withdrawal-approval-details-grid'>
                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatDate(withdrawalDetails.withdrawalApprovalDate)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>
                      Redemption Approval Date
                    </div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatDate(withdrawalDetails.amountReceivableDate)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>
                      Amount Receivable Date
                    </div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatCurrencyFromDollar(withdrawalAmount)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>Redemption Amount</div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatCurrency(withdrawalDetails.grossMoneyToBeReceived)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>
                      Gross Money to be Received
                    </div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatCurrency(withdrawalDetails.charges?.profitSharing)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>Profit Sharing</div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatCurrency(withdrawalDetails.charges?.exitLoad)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>Exit Load</div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatCurrency(withdrawalDetails.netMoneyToBeReceived)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>
                      Net Money to be Received
                    </div>
                  </div>
                </div>

                <div className='withdrawal-approval-details-card'>
                  <div className='withdrawal-approval-details-card-content'>
                    <div className='withdrawal-approval-details-card-value'>
                      {formatPercentage(withdrawalDetails.effectiveReturn)}
                    </div>
                    <div className='withdrawal-approval-details-card-text'>Effective Return</div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export { WithdrawalAmountOpportunityCard }
